import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FolderKanban, X, CalendarClock, AlertTriangle, Link as LinkIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SiteButton } from "@/components/brand/SiteButton";

const STATUS_BADGE = {
  planning: "border-brand-azure/40 bg-brand-azure/10 text-brand-azure",
  active: "border-brand-emerald/40 bg-brand-emerald/10 text-brand-emerald",
  "on-hold": "border-yellow-400/40 bg-yellow-400/10 text-yellow-300",
  completed: "border-brand-mist/40 bg-brand-mist/10 text-brand-mist",
  archived: "border-brand-mist/20 bg-brand-mist/5 text-brand-mist/60",
};

const SERVICE_TYPES = ["general", "seo", "web-development", "mobile", "ai", "design"];

const NewProjectModal = ({ team, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", service_type: "general", description: "", deadline: "", team: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleTeam = (id) =>
    setForm((f) => ({ ...f, team: f.team.includes(id) ? f.team.filter((t) => t !== id) : [...f.team, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, deadline: form.deadline || null };
      const r = await api.post("/pms/projects", payload);
      onCreated(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="project-new-modal">
      <form onSubmit={submit} className="w-full max-w-lg space-y-4 rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-snow">New project</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Project name" data-testid="project-name-input"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="project-type-select"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
          {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={3} data-testid="project-desc-input"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <div>
          <label className="mb-1.5 block text-xs text-brand-mist">Deadline (optional)</label>
          <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="project-deadline-input"
            className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-brand-mist">Assign team</label>
          <div className="flex flex-wrap gap-2">
            {team.map((m) => (
              <button type="button" key={m.id} onClick={() => toggleTeam(m.id)} data-testid={`project-team-${m.id}`}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${form.team.includes(m.id) ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald" : "border-brand-mist/20 text-brand-mist hover:text-brand-snow"}`}>
                {m.name || m.email}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="text-sm text-red-400" data-testid="project-new-error">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <SiteButton type="button" variant="ghost" onClick={onClose}>Cancel</SiteButton>
          <SiteButton type="submit" disabled={saving} testid="project-create-submit">{saving ? "Creating…" : "Create project"}</SiteButton>
        </div>
      </form>
    </div>
  );
};

const DeadlinesWidget = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/pms/deadlines").then((r) => setData(r.data)).catch(() => setData({ overdue_tasks: [], upcoming_tasks: [], project_deadlines: [] }));
  }, []);
  if (!data) return null;
  const { overdue_tasks, upcoming_tasks } = data;
  if (overdue_tasks.length === 0 && upcoming_tasks.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="deadlines-widget">
      <div className="rounded-xl border border-red-400/20 bg-red-400/5 p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-red-300"><AlertTriangle size={15} /> Overdue tasks ({overdue_tasks.length})</h3>
        <ul className="mt-3 space-y-2">
          {overdue_tasks.slice(0, 6).map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
              <Link to={`/admin/projects/${t.project_id}`} className="truncate text-brand-snow hover:text-brand-emerald">{t.title}</Link>
              <span className="shrink-0 text-[11px] text-red-300">{t.due_date}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-brand-azure/20 bg-brand-azure/5 p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-brand-azure"><CalendarClock size={15} /> Upcoming deadlines ({upcoming_tasks.length})</h3>
        <ul className="mt-3 space-y-2">
          {upcoming_tasks.slice(0, 6).map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
              <Link to={`/admin/projects/${t.project_id}`} className="truncate text-brand-snow hover:text-brand-emerald">{t.title}</Link>
              <span className="shrink-0 text-[11px] text-brand-mist">{t.due_date}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};


const AdminProjects = () => {
  const [projects, setProjects] = useState(null);
  const [team, setTeam] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    api.get("/pms/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  };
  useEffect(() => {
    load();
    api.get("/pms/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-projects">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-brand-snow">Projects</h1>
          <p className="mt-1 text-sm text-brand-mist">Internal project management — SEO, web development and team delivery.</p>
        </div>
        <SiteButton onClick={() => setShowNew(true)} testid="project-new-button"><Plus size={16} className="mr-1.5 inline" /> New project</SiteButton>
      </div>

      <DeadlinesWidget />

      {projects === null && <p className="text-sm text-brand-mist">Loading…</p>}
      {projects !== null && projects.length === 0 && (
        <div className="rounded-2xl border border-dashed border-brand-mist/20 p-12 text-center" data-testid="projects-empty">
          <FolderKanban className="mx-auto text-brand-mist/40" size={32} />
          <p className="mt-3 text-sm text-brand-mist">No projects yet. Create your first project to get started.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
        {(projects || []).map((p) => {
          const pct = p.task_total ? Math.round((p.task_done / p.task_total) * 100) : 0;
          return (
            <Link key={p.id} to={`/admin/projects/${p.id}`} data-testid={`project-card-${p.id}`}
              className="group rounded-2xl border border-brand-mist/15 bg-brand-charcoal p-5 transition-colors hover:border-brand-emerald/40">
              <div className="flex items-start justify-between gap-3">
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_BADGE[p.status] || STATUS_BADGE.planning}`}>{p.status}</span>
                <span className="text-[11px] text-brand-mist">{p.service_type}</span>
              </div>
              <h2 className="mt-3 font-display text-lg font-semibold text-brand-snow group-hover:text-brand-emerald">{p.name}</h2>
              <p className="mt-1 line-clamp-2 text-sm text-brand-mist">{p.description || "No description"}</p>
              <div className="mt-4">
                <div className="flex justify-between text-[11px] text-brand-mist">
                  <span>{p.task_done}/{p.task_total} tasks</span>
                  <span>{pct}%</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-brand-black/60">
                  <div className="h-full rounded-full bg-brand-emerald transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {showNew && <NewProjectModal team={team} onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); load(); }} />}
    </div>
  );
};

export default AdminProjects;
